import React, { useState, useRef, useEffect } from 'react';
import { CloseOutlined, MessageOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { useReviews } from '../Review/hook/useReviews';
import { ReviewItem } from '../Review/components/ReviewItem';
import { StarDisplay } from '../Review/components/StarDisplay';
import axios from '../../../services/axiosInstance';
import ReactDOM from "react-dom";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ficheId?: number;
  locationName?: string;
  accountName?: string;       
  subjectEmail?: string;     
}

export const ReviewsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  ficheId,
  locationName,
  accountName,
  subjectEmail,
}) => {
  const { user } = useAuth();
  const modalRef = useRef<HTMLDivElement>(null);

  const [replyOpenFor, setReplyOpenFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

   const { reviewsData, loading, error, refetch } = useReviews({
    isOpen,
    locationName,
    accountName,
    subjectEmail,
  });

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose();
    };
    if (isOpen) document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, onClose]);

   const handleReplySubmit = async (review: any) => {
  if (!replyText.trim() || isSubmitting) return;
  setIsSubmitting(true);
  try {
    await axios.post('/api/gbp/reviews/reply', {
      replyComment: replyText,
      reviewId:     review.raw?.reviewId ?? review.id,
      locationName,
      reviewName:   review.raw?.name,
      subjectEmail,  
    });
    await refetch();
    toast.success('Réponse publiée avec succès');
    setReplyText('');
    setReplyOpenFor(null);
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'Erreur lors de la publication');
  } finally {
    setIsSubmitting(false);
  }
};

const handleDeleteReply = async (review: any) => {
  try {
    const params: any = { reviewName: review.raw?.name };
    if (!params.reviewName) {
      params.reviewId      = review.raw?.reviewId ?? review.id;
      params.locationName  = locationName;
    }
     params.subjectEmail = subjectEmail;

    await axios.delete('/api/gbp/reviews/reply', { params });
    await refetch();
    toast.info('Réponse supprimée');
  } catch {
    toast.error('Impossible de supprimer la réponse');
  }
};

  if (!isOpen) return null;

  const displayAverage =
    reviewsData?.averageRating != null
      ? Number(Number(reviewsData.averageRating).toFixed(1))
      : null;

   const displayTotal = reviewsData?.totalReviews ?? 0;


  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="flex flex-col bg-white rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden
          shadow-[0_24px_64px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.06)]
          animate-[gbpFadeIn_0.22s_ease-out]"
      >
         <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-orange-50 text-orange-500 flex-shrink-0">
            <MessageOutlined style={{ fontSize: 17 }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-slate-800 leading-tight m-0">Gestion des avis</h2>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full uppercase tracking-wider mt-0.5">
              <SafetyCertificateOutlined /> Google Business
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-100 text-slate-400 hover:bg-rose-50 hover:text-rose-500 hover:border-rose-100 transition-all duration-150"
          >
            <CloseOutlined style={{ fontSize: 13 }} />
          </button>
        </div>

         <div className="flex gap-3 px-6 py-4 bg-slate-50/60 border-b border-slate-100 flex-shrink-0">
          <div className="flex-1 bg-white rounded-xl border border-slate-100 px-4 py-3 flex items-center gap-3 shadow-sm">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Note moyenne</p>
              <div className="flex items-end gap-2">
                <span className="text-2xl font-black text-slate-800 leading-none">
                  {displayAverage ?? '—'}
                </span>
                <div className="pb-0.5">
                  <StarDisplay rating={displayAverage ? Math.round(displayAverage) : 0} />
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white rounded-xl border border-slate-100 px-4 py-3 flex items-center gap-3 shadow-sm">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Volume d'avis</p>
              <div className="flex items-end gap-2">
                <span className="text-2xl font-black text-slate-800 leading-none">{displayTotal}</span>
                <span className="text-xs text-slate-400 pb-0.5 font-medium">témoignages</span>
              </div>
            </div>
          </div>
        </div>

         <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="relative h-10 w-10">
                <div className="absolute inset-0 rounded-full border-[3px] border-slate-100" />
                <div className="absolute inset-0 rounded-full border-[3px] border-[#0F52BA] border-t-transparent animate-spin" />
              </div>
              <span className="text-sm font-medium text-slate-400">Chargement des avis…</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 px-4 py-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-sm font-medium">
              <span>⚠️</span> {error}
            </div>
          ) : reviewsData?.reviews.length ? (
            reviewsData.reviews.map(r => (
              <div key={r.id} className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:border-blue-200 hover:shadow-sm transition-all duration-150">
                <ReviewItem
                  review={r}
                  replyOpenFor={replyOpenFor}
                  replyText={replyText}
                  setReplyOpenFor={setReplyOpenFor}
                  setReplyText={setReplyText}
                  onReplySubmit={handleReplySubmit}
                  onDeleteReply={handleDeleteReply}
                  isAdmin={user?.isAdmin || false}
                />
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3 text-xl opacity-40">✨</div>
              <p className="text-sm font-semibold text-slate-600">Aucun avis disponible</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">Les avis de vos clients apparaîtront ici une fois collectés sur Google.</p>
            </div>
          )}
        </div>

         <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-shrink-0">
          <span className="text-[11px] text-slate-300 italic">
            Mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </span>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl text-xs font-semibold bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100 transition-all duration-150"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReviewsModal;